import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
    alpha,
    Badge,
    Box,
    Divider,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Typography,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import GradingIcon from "@mui/icons-material/GradeOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { getTeacherStats } from "../../redux/teacherRelated/teacherToolsHandle";
import { teacherNeedsOnboarding } from "../../utils/teacherOnboarding";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import { useCloseMobileNav, useNavCollapsed } from "../../components/DashboardLayoutContext";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { getTeacherSubjects } from "../../utils/teacherSubjects";
import { useHomeroomTeacherTerm } from "../../hooks/useHomeroomTeacherTerm";
import { BURSAR_FINANCE_NAV } from "../../config/bursarFinanceNav";
import { LIBRARIAN_LIBRARY_NAV } from "../../config/librarianLibraryNav";
import {
    getStaffRoleFromUser,
    getStaffRoleLabel,
    adminLibraryManagedByLibrarian,
    isBursarUser,
    isLibrarianUser,
    isTeachingStaff,
    showsTeachingPortal,
} from "../../utils/staffRoles";

function NavRow({ to, label, Icon, selected, tone, badge, badgeColor = "primary" }) {
    const isDanger = tone === "danger";
    const showBadge = badge !== undefined && badge !== null && badge !== "";
    const closeMobileNav = useCloseMobileNav();
    const collapsed = useNavCollapsed();

    return (
        <ListItemButton
            title={collapsed ? label : undefined}
            component={Link}
            to={to}
            selected={selected}
            onClick={closeMobileNav}
            sx={{
                borderRadius: 2,
                mx: 0.5,
                mb: 0.2,
                py: 0.75,
                pl: 2,
                transition: (t) =>
                    t.transitions.create(["background-color", "color", "transform"], {
                        duration: t.transitions.duration.shorter,
                    }),
                "&:hover": {
                    bgcolor: (t) => alpha(isDanger ? t.palette.error.main : t.palette.primary.main, 0.08),
                    transform: "translateX(2px)",
                },
                "&.Mui-selected": {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    color: "primary.main",
                    boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                },
                ...(isDanger && {
                    "&.Mui-selected": {
                        bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                        color: "error.main",
                        boxShadow: (t) => `inset 3px 0 0 ${t.palette.error.main}`,
                    },
                }),
            }}
        >
            <ListItemIcon sx={{ minWidth: 38, color: selected ? "inherit" : "text.secondary" }}>
                <Badge
                    badgeContent={showBadge ? badge : 0}
                    color={badgeColor}
                    invisible={!showBadge}
                    max={99}
                    sx={{
                        "& .MuiBadge-badge": {
                            fontSize: "0.65rem",
                            height: 18,
                            minWidth: 18,
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: (t) =>
                                selected
                                    ? alpha(t.palette.primary.main, 0.15)
                                    : alpha(t.palette.action.hover, 0.45),
                        }}
                    >
                        <Icon sx={{ fontSize: "1.1rem" }} />
                    </Box>
                </Badge>
            </ListItemIcon>
            {!collapsed ? (
                <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                        fontWeight: selected ? 700 : 500,
                        fontSize: "0.8125rem",
                        lineHeight: 1.25,
                    }}
                />
            ) : null}
        </ListItemButton>
    );
}

const TeacherSideBar = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const path = location.pathname;

    const { currentUser } = useSelector((state) => state.user);
    const { teacherStats } = useSelector((state) => state.teacherTools);
    const { teachersList } = useSelector((state) => state.teacher);

    const staffRole = getStaffRoleFromUser(currentUser);
    const roleLabel = getStaffRoleLabel(staffRole);
    const bursarMode = isBursarUser(currentUser);
    const librarianMode = isLibrarianUser(currentUser);
    const teachingMode = isTeachingStaff(staffRole);
    const teachingPortal = showsTeachingPortal(currentUser);
    const schoolHasLibrarian = adminLibraryManagedByLibrarian(currentUser, teachersList, {
        requireProfileCompleted: true,
    });

    const teacherId = currentUser?._id;
    const { className: activeClassLabel, subjects } = useTeacherTeaching();
    const { homeroomLabel, roleIsHomeroom, hasHomeroomClass, needsHomeroomSetup } = useFormTeacherHomeroom();
    const homeroomTerm = useHomeroomTeacherTerm();
    const collapsed = useNavCollapsed();
    const classLabel =
        subjects.length > 1 ? `${activeClassLabel} (active)` : activeClassLabel || currentUser?.teachSclass?.sclassName;

    useEffect(() => {
        if (teacherId && teachingPortal && !bursarMode) {
            dispatch(getTeacherStats(teacherId));
        }
    }, [dispatch, teacherId, teachingPortal, bursarMode]);

    const assignmentsPosted = teacherStats?.assignmentsPosted ?? 0;
    const leaveBalance = teacherStats?.leaveBalance ?? 0;
    const leaveBadgeColor = leaveBalance > 5 ? "success" : "warning";

    const homeSelected = path === "/" || path === "/Teacher/dashboard" || path.endsWith("/Teacher/dashboard");
    const onboarding = teacherNeedsOnboarding(currentUser);

    const showHomeroomNav = hasHomeroomClass || roleIsHomeroom;
    const hasTeachingSubjects = getTeacherSubjects(currentUser).length > 0;
    const showDisciplinaryNav = teachingMode && !bursarMode && (hasHomeroomClass || hasTeachingSubjects);

    const myClassNav =
        teachingMode && !bursarMode && showHomeroomNav
            ? [
                  {
                      to: "/Teacher/my-class",
                      label: hasHomeroomClass && homeroomLabel ? `My class (${homeroomLabel})` : "My class",
                      Icon: ClassOutlinedIcon,
                      match: (p) =>
                          p.startsWith("/Teacher/my-class") ||
                          p.startsWith("/Teacher/enroll-student") ||
                          p.startsWith("/Teacher/form-teacher-results") ||
                          p.startsWith("/Teacher/report-card"),
                      badge: needsHomeroomSetup ? "!" : undefined,
                      badgeColor: "warning",
                  },
                  {
                      to: "/Teacher/my-class/subjects",
                      label: "Class subjects & electives",
                      Icon: MenuBookOutlinedIcon,
                      match: (p) => p.startsWith("/Teacher/my-class/subjects"),
                  },
                  {
                      to: "/Teacher/my-class/resources",
                      label: "Class resources (term)",
                      Icon: FolderSharedOutlinedIcon,
                      match: (p) => p.startsWith("/Teacher/my-class/resources"),
                  },
              ]
            : [];

    const teachingNav = [
        {
            to: "/Teacher/my-subjects",
            label: "My subjects (this term)",
            Icon: MenuBookOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/my-subjects"),
        },
        {
            to: "/Teacher/class",
            label: classLabel ? `Subject class (${classLabel})` : "Subject class roster",
            Icon: ClassOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/class") && !p.startsWith("/Teacher/enroll"),
        },
        {
            to: "/Teacher/marks-entry",
            label: "Submit subject marks",
            Icon: GradingIcon,
            match: (p) => p.startsWith("/Teacher/marks-entry"),
        },
        {
            to: "/Teacher/ca-assessment",
            label: "Continuous Assessment",
            Icon: AssessmentOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/ca-assessment"),
        },
        {
            to: "/Teacher/attendance-entry",
            label: "Record attendance",
            Icon: FactCheckOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/attendance-entry"),
        },
        {
            to: "/Teacher/results",
            label: "Published class results",
            Icon: GradingIcon,
            match: (p) => p.startsWith("/Teacher/results"),
        },
        {
            to: "/Teacher/schemes",
            label: "Scheme of work",
            Icon: MenuBookOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/schemes"),
        },
        {
            to: "/Teacher/lessons",
            label: "Lesson plans",
            Icon: EventNoteOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/lessons"),
        },
        {
            to: "/Teacher/assignments",
            label: "Assignments",
            Icon: AssignmentOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/assignments"),
            badge: assignmentsPosted,
            badgeColor: "success",
        },
        {
            to: "/Teacher/resources",
            label: "Class resources",
            Icon: FolderSharedOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/resources"),
        },
        ...(teachingMode && !bursarMode
            ? [
                  {
                      to: "/Teacher/material-requests",
                      label: "Material requests",
                      Icon: InventoryOutlinedIcon,
                      match: (p) => p.startsWith("/Teacher/material-requests"),
                  },
              ]
            : []),
        ...(!schoolHasLibrarian
            ? [
                  {
                      to: "/Teacher/library",
                      label: "Library & TLMs",
                      Icon: MenuBookOutlinedIcon,
                      match: (p) => p.startsWith("/Teacher/library"),
                  },
              ]
            : []),
        {
            to: "/Teacher/analytics",
            label: "Class analytics",
            Icon: InsightsOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/analytics"),
        },
        {
            to: "/Teacher/maneb",
            label: "MANEB exams",
            Icon: GradingIcon,
            match: (p) => p.startsWith("/Teacher/maneb"),
        },
        {
            to: "/Teacher/timetable",
            label: "My timetable",
            Icon: AccessTimeOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/timetable"),
        },
        {
            to: "/Teacher/academic-calendar",
            label: "Academic calendar",
            Icon: EventAvailableOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/academic-calendar"),
        },
        {
            to: "/Teacher/leave",
            label: "Apply for leave",
            Icon: EventBusyOutlinedIcon,
            match: (p) => p.startsWith("/Teacher/leave"),
            badge: leaveBalance,
            badgeColor: leaveBadgeColor,
        },
        ...(showDisciplinaryNav
            ? [
                  {
                      to: "/Teacher/disciplinary",
                      label: "Disciplinary",
                      Icon: GavelOutlinedIcon,
                      match: (p) => p.startsWith("/Teacher/disciplinary"),
                  },
              ]
            : []),
    ];

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.dark, 0.03)} 0%, ${t.palette.background.paper} 16%)`
                        : t.palette.background.paper,
            }}
        >
            {!collapsed ? (
                <>
                    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                            {bursarMode
                                ? "Bursar office"
                                : librarianMode && teachingPortal
                                  ? "Library & teaching"
                                  : librarianMode
                                    ? "Library office"
                                    : `${roleLabel} portal`}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.25, mt: 0.25 }}>
                            {currentUser?.name || "Dashboard"}
                        </Typography>
                    </Box>
                    <Divider sx={{ opacity: 0.6, mb: 0.5 }} />
                </>
            ) : null}

            <Box sx={{ flex: 1, overflow: "auto", py: 0.5 }}>
            {!collapsed && onboarding && !bursarMode && !librarianMode ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1, fontSize: "0.8rem" }}>
                    Complete your profile to unlock the dashboard and teaching tools.
                </Typography>
            ) : null}
            {!collapsed && onboarding && bursarMode ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1, fontSize: "0.8rem" }}>
                    Finish your profile when you can (Personal tab). School finance is available below.
                </Typography>
            ) : null}
            {!collapsed && onboarding && librarianMode ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1, fontSize: "0.8rem" }}>
                    Finish your profile when you can (Personal tab).
                    {teachingPortal
                        ? " Library and teaching tools are available below."
                        : " Library tools are available below."}
                </Typography>
            ) : null}
            {(!onboarding || bursarMode || librarianMode) ? (
                <>
            <NavRow to="/Teacher/dashboard" label="Home" Icon={HomeOutlinedIcon} selected={homeSelected} />

            {librarianMode ? (
                <>
                    {!collapsed ? (
                        <ListSubheader sx={{ lineHeight: "28px", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 0.8 }}>
                            Library & TLMs
                        </ListSubheader>
                    ) : null}
                    {LIBRARIAN_LIBRARY_NAV.map((item) => (
                        <NavRow
                            key={item.to}
                            to={item.to}
                            label={item.label}
                            Icon={MenuBookOutlinedIcon}
                            selected={item.match(path)}
                        />
                    ))}
                </>
            ) : null}

            {bursarMode ? (
                <>
                    {!collapsed ? (
                        <ListSubheader sx={{ lineHeight: "28px", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 0.8 }}>
                            School finance
                        </ListSubheader>
                    ) : null}
                    {BURSAR_FINANCE_NAV.map((item) => {
                        const iconMap = {
                            "/Teacher/fees/structures": AccountBalanceOutlinedIcon,
                            "/Teacher/fees/collection": PaymentsOutlinedIcon,
                            "/Teacher/fees/defaulters": WarningAmberOutlinedIcon,
                            "/Teacher/fees/reports": BarChartOutlinedIcon,
                            "/Teacher/expenses/log": ReceiptLongOutlinedIcon,
                            "/Teacher/expenses/budgets": SavingsOutlinedIcon,
                            "/Teacher/expenses/summary": AssessmentOutlinedIcon,
                        };
                        const Icon = iconMap[item.to] || PaymentsOutlinedIcon;
                        return (
                            <NavRow
                                key={item.to}
                                to={item.to}
                                label={item.label}
                                Icon={Icon}
                                selected={item.match(path)}
                            />
                        );
                    })}
                </>
            ) : null}

            {teachingMode && myClassNav.length > 0 ? (
                <>
                    {!collapsed ? (
                        <ListSubheader sx={{ lineHeight: "28px", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 0.8 }}>
                            My class
                        </ListSubheader>
                    ) : null}
                    {myClassNav.map(({ to, label, Icon, match, badge, badgeColor }) => (
                        <NavRow
                            key={to}
                            to={to}
                            label={label}
                            Icon={Icon}
                            selected={match(path)}
                            badge={badge}
                            badgeColor={badgeColor}
                        />
                    ))}
                    <Divider sx={{ my: 1, mx: 1.5 }} />
                </>
            ) : null}

            {teachingPortal && !bursarMode ? (
                <>
            {!collapsed ? (
                <ListSubheader sx={{ lineHeight: "28px", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 0.8 }}>
                    Subject teaching
                </ListSubheader>
            ) : null}
            {subjects.length === 0 && !collapsed ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 0.5, fontSize: "0.8rem" }}>
                    No subjects assigned yet. Ask your director to link subjects under Staff.
                </Typography>
            ) : null}
            {teachingNav
                .filter((item) => !(librarianMode && item.to === "/Teacher/library"))
                .map(({ to, label, Icon, match, badge, badgeColor }) => (
                <React.Fragment key={to}>
                    {to !== "/Teacher/schemes" ? (
                        <NavRow
                            to={to}
                            label={label}
                            Icon={Icon}
                            selected={match(path)}
                            badge={badge}
                            badgeColor={badgeColor}
                        />
                    ) : null}
                    {to === "/Teacher/results" ? (
                        <>
                            <Divider sx={{ my: 1, mx: 1.5 }} />
                            <ListSubheader
                                sx={{ lineHeight: "28px", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 0.8 }}
                            >
                                Teaching Tools
                            </ListSubheader>
                            <NavRow
                                to="/Teacher/schemes"
                                label="Schemes & Records"
                                Icon={EventNoteOutlinedIcon}
                                selected={path.startsWith("/Teacher/schemes")}
                            />
                        </>
                    ) : null}
                </React.Fragment>
            ))}

            <Divider sx={{ my: 1, mx: 1.5 }} />
                </>
            ) : null}
            {teachingMode && !bursarMode && roleIsHomeroom && !homeroomLabel && !collapsed ? (
                <Typography variant="body2" color="warning.main" sx={{ px: 2, py: 1, fontSize: "0.8rem" }}>
                    You are a {homeroomTerm.theRole} — ask your director to assign you on a class under All classes.
                </Typography>
            ) : null}

            {!bursarMode ? (
                <>
                    <Divider sx={{ my: 1, mx: 1.5 }} />
                    <NavRow
                        to="/Teacher/complain"
                        label="Complain"
                        Icon={AnnouncementOutlinedIcon}
                        selected={path.startsWith("/Teacher/complain")}
                    />
                </>
            ) : null}
                </>
            ) : null}

            {!collapsed ? (
                <ListSubheader sx={{ lineHeight: "28px", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 0.8 }}>
                    Account
                </ListSubheader>
            ) : null}
            {!onboarding && !bursarMode ? (
                <NavRow
                    to="/Teacher/salary"
                    label="My salary"
                    Icon={PaymentsOutlinedIcon}
                    selected={path.startsWith("/Teacher/salary")}
                />
            ) : null}
            <NavRow
                to="/Teacher/profile"
                label="Profile"
                Icon={AccountCircleOutlinedIcon}
                selected={path.startsWith("/Teacher/profile")}
            />
            <NavRow
                to="/logout"
                label="Logout"
                Icon={ExitToAppIcon}
                selected={path.startsWith("/logout")}
                tone="danger"
            />
            </Box>
        </Box>
    );
};

export default TeacherSideBar;
